<template>
  <div class="main-page">
    <template v-if="showHeader">
      <section class="page-section">
        <div class="container-fluid">
          <div class="grid justify-content-between">
            <div class="col">
              <div class="">
                <div class="surface-100 p-3 text-lg font-bold">Activities</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
    <section class="page-section">
      <div class="container-fluid">
        <div class="grid">
          <div class="col comp-grid">
            <div class="">
              <div class="flex align-items-center justify-content-around">
                <div v-if="searchText" :class="filterTagClass">
                  Search
                  <Chip
                    class="font-medium px-2 py-1"
                    removable
                    @remove="clearSearch()"
                    >{{ searchText }}</Chip
                  >
                </div>
              </div>
              <div class="">
                <div>
                  <template v-if="showBreadcrumbs && $route.query.tag">
                    <Breadcrumb
                      :home="{ icon: 'pi pi-home', to: '/activities' }"
                      :model="pageBreadCrumb"
                    />
                  </template>
                  <div class="relative-position">
                    <div class="grid">
                      <!-- Master Page Start -->
                      <div class="col-12">
                        <div class="">
                          <div class="page-records" v-if="!loading">
                            <div class="grid grid-nogutter">
                              <div v-for="(data, i) of records" :key="i">
                                <div class="col-12">
                                  <div class="card mb-2">
                                    <div class="grid">
                                      <div class="col">
                                        <div class="text-500" lines="2">
                                          <span
                                            class="cursor-pointer mr-2"
                                            @click="
                                              app.navigateTo(
                                                `/users/view/${data.user_id}`,
                                                '_blank'
                                              )
                                            "
                                            >{{ data.users_fullname }}
                                          </span>
                                          <span
                                            class="text-blue-700 cursor-pointer"
                                            v-ripple
                                            @click="
                                              app.navigateTo(
                                                `/complaints/view/${data.ticket_id}`
                                              )
                                            "
                                          >
                                            {{ data.action_taken }}</span
                                          >
                                          •
                                          <span class="text-sm">
                                            {{
                                              $utils.relativeDate(
                                                data.date_created
                                              )
                                            }}
                                          </span>
                                        </div>
                                      </div>
                                      <div
                                        class="col-fixed text-right"
                                        style="width: auto"
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <!-- page loading indicator -->
                          <template v-if="loading">
                            <div>
                              <table class="p-datatable-table w-full">
                                <tbody>
                                  <tr v-for="n in 5" :key="n">
                                    <td class="text-left" style="width: 100%">
                                      <Skeleton height="3rem" class="m-2" />
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </template>
                          <!-- end of page loading indicator-->
                          <!-- Empty record -->
                          <template v-if="pageReady && !records.length">
                            <div
                              class="p-3 my-3 text-500 text-lg font-medium text-center"
                            >
                              No activities found
                            </div>
                          </template>
                          <!-- end of empty record-->
                          <!-- pagination component-->
                          <template v-if="showFooter">
                            <div class="">
                              <div class="flex justify-content-between">
                                <div class="flex-grow-0">
                                  <div class="flex"></div>
                                </div>
                                <div class="flex-grow-1">
                                  <div
                                    v-if="pageReady && totalPages > 1"
                                    class="flex justify-content-center"
                                  >
                                    <div class="m-2">
                                      <Button
                                        :disabled="pagination.page <= 1"
                                        label="Previous"
                                        class="p-button-sm"
                                        @click="loadPreviousPage()"
                                        icon="pi pi-arrow-left"
                                      />
                                    </div>
                                    <div class="m-2">
                                      <Button
                                        :disabled="
                                          pagination.page >= totalPages
                                        "
                                        label="Next"
                                        class="p-button-sm"
                                        @click="loadNextPage()"
                                        icon="pi pi-arrow-right"
                                        icon-pos="right"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                          <!-- end of pagination component-->
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { reactive, toRefs, onMounted } from "vue";
import { utils } from "@/utils";
import { useApp } from "@/composables/app.js";
import { useListPage } from "@/composables/listpage.js";
//[--PAGE-IMPORT-STATEMENT--]
const props = defineProps({
  primaryKey: {
    type: String,
    default: "activity_id",
  },
  pageName: {
    type: String,
    default: "activities",
  },
  routeName: {
    type: String,
    default: "activitieslist",
  },
  apiPath: {
    type: String,
    default: "activities/index",
  },
  paginate: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  showBreadcrumbs: {
    type: Boolean,
    default: true,
  },
  exportButton: {
    type: Boolean,
    default: true,
  },
  importButton: {
    type: Boolean,
    default: false,
  },
  multiCheckbox: {
    type: Boolean,
    default: false,
  },
  emptyRecordMsg: {
    type: String,
    default: "No record found",
  },
  titleBeforeDelete: {
    type: String,
    default: "Delete record",
  },
  msgBeforeDelete: {
    type: String,
    default: "Are you sure you want to delete this record?",
  },
  msgAfterDelete: {
    type: String,
    default: "Record deleted successfully",
  },
  page: {
    type: Number,
    default: 1,
  },
  limit: {
    type: Number,
    default: 3,
  },
  mergeRecords: {
    // for infinite loading
    type: Boolean,
    default: false,
  },
  search: {
    type: String,
    default: "",
  },
  fieldName: null,
  fieldValue: null,
  sortBy: {
    type: String,
    default: "",
  },
  sortType: {
    type: String,
    default: "", //desc or asc
  },
  isSubPage: {
    type: Boolean,
    default: false,
  },
  filterTagClass: {
    type: String,
    default:
      "surface-card p-2 text-500 flex-grow-1 text-center m-1 mb-3 flex-grow-1 text-center",
  },
});
const app = useApp();
const filters = reactive({});
//init list page hook
const page = useListPage(props, filters);
//page state
const {
  totalRecords, // total records from api - Number
  recordCount, // current record count - Number
  loading, // Api loading state - Boolean
  selectedItems, // Data table selected items -Array
  pagination, //Pagination object - Object
  searchText, // search text - String
  pageReady, // api data loaded successfully
} = toRefs(page.state);
//page computed propties
const {
  records, // page record from store - Array
  apiUrl, // current api path - URL
  currentRecord, // master detail selected record - Object
  pageBreadCrumb, // get page navigation paths - Object
  canLoadMore, // if api has more data for loading - Boolean
  finishedLoading, // if api has finished loading - Boolean
  totalPages, // total number of pages from api - Number
  recordsPosition, // current record position from api - Number
} = page.computedProps;
//page methods
const {
  load, // load data from api
  reload, // reset pagination and load data
  loadNextPage, // load next page
  loadPreviousPage, // load previous page
  exportPage, // export page records - args = (exportFormats, apiUrl, pageName)
  clearSearch, // clear input search
  debounce, // debounce input search
  onSort, // reset page number before sort from api
  deleteItem, // delete item by id or selected items - args = (id) or (selectedItems)
  setCurrentRecord, // master detail set current record
  isCurrentRecord, // master detail
  removeFilter, // remove page filter item - args = (filter.propertyname)
  getFilterLabel, // get filter item display label - args = (filter.propertyname)
  filterHasValue, //check if filter item has value - args = (filter.propertyname)
  importComplete, // reload page after data import
} = page.methods;
function getActionMenuModel(data) {
  return [];
}
onMounted(() => {
  const pageTitle = "Activities";
  app.setPageTitle(props.routeName, pageTitle);
  load();
});
</script>
<style scoped></style>
<style scoped></style>
